// src/store/auth.js
// Simple reactive auth store — no Vuex/Pinia needed

import { reactive } from 'vue'

const TOKEN_KEY = 'babilonia_auth_token'
const USER_KEY  = 'babilonia_auth_user'
const REFRESH_KEY = 'babilonia_refresh_token'

const state = reactive({
  token:        localStorage.getItem(TOKEN_KEY)  || null,
  user:         JSON.parse(localStorage.getItem(USER_KEY) || 'null'),
  refreshToken: localStorage.getItem(REFRESH_KEY) || null,
})

// Track in-flight refresh to avoid parallel refresh calls
let _refreshPromise = null

export const authStore = {
  state,

  get isLoggedIn() {
    return !!state.token && !!state.user
  },

  get currentUser() {
    return state.user
  },

  get token() {
    return state.token
  },

  hasPermission(permission) {
    if (!state.user) return false
    if (state.user.role === 'admin') return true
    return (state.user.permissions || []).includes(permission)
  },

  isAdmin() {
    return state.user?.role === 'admin'
  },

  login(token, user, refreshToken = null) {
    state.token = token
    state.user  = user
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    if (refreshToken) {
      state.refreshToken = refreshToken
      localStorage.setItem(REFRESH_KEY, refreshToken)
    }
  },

  logout() {
    // Notify backend to invalidate refresh token (fire-and-forget)
    if (state.refreshToken) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${state.token}`
        },
        body: JSON.stringify({ refresh_token: state.refreshToken })
      }).catch(() => {})
    }
    state.token        = null
    state.user         = null
    state.refreshToken = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },

  // Exchange refresh token for a new access token + fresh permissions from DB
  async refreshAccessToken() {
    if (!state.refreshToken) return false
    if (_refreshPromise) return _refreshPromise

    _refreshPromise = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: state.refreshToken })
        })
        if (res.ok) {
          const data = await res.json()
          this.login(data.token, data.user, data.refresh_token)
          return true
        } else {
          // Refresh token is invalid/expired — force logout
          this.logout()
          return false
        }
      } catch {
        return false
      } finally {
        _refreshPromise = null
      }
    })()

    return _refreshPromise
  },

  // Drop-in replacement for fetch() — auto-refreshes on 401 and retries once
  async apiFetch(url, options = {}) {
    const makeRequest = (token) => fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`
      }
    })

    let res = await makeRequest(state.token)

    if (res.status === 401 && state.refreshToken) {
      const refreshed = await this.refreshAccessToken()
      if (refreshed) {
        res = await makeRequest(state.token)
      } else {
        window.location.href = '/login'
      }
    }

    return res
  },

  async fetchMe() {
    if (!state.token) return null
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      if (res.ok) {
        const data = await res.json()
        state.user = data.user
        localStorage.setItem(USER_KEY, JSON.stringify(data.user))
        return data.user
      } else {
        this.logout()
        return null
      }
    } catch {
      return null
    }
  }
}

export default authStore